import PropTypes from 'prop-types'
import React, {
  useCallback, useMemo, useState, useEffect,
} from 'react'
import debounce from 'lodash/debounce'
import styles from './LabelField.scss'
import Parameter from '../../../views/Create/components/Parameter'
import { validateWithDependencies } from '../../utils/dependencies'

const labelFieldPropTypes = {
  disabled: PropTypes.bool.isRequired,
  field: PropTypes.shape({
    dependencies: PropTypes.arrayOf(PropTypes.object).isRequired,
    field: PropTypes.object.isRequired,
    formData: PropTypes.object.isRequired,
    id: PropTypes.string.isRequired,
    isDisabled: PropTypes.bool,
    isRequired: PropTypes.bool,
    name: PropTypes.string.isRequired,
  }).isRequired,
  isBulkUpdateModal: PropTypes.bool,
  modifiedFields: PropTypes.arrayOf(PropTypes.string).isRequired,
  setModifiedFields: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  validateLabel: PropTypes.func.isRequired,
}

const labelFieldDefaultProps = {
  isBulkUpdateModal: false,
}

function LabelField({
  field, disabled, isBulkUpdateModal, modifiedFields, setModifiedFields, setValue, validateLabel,
}) {
  const [currentFieldValue, setCurrentFieldValue] = useState('')
  const [localValue, setLocalValue] = useState(field.formData.value)

  useEffect(() => {
    setLocalValue(field.formData.value)
  }, [field.formData.value])

  const debouncedSetValue = useMemo(
    () => debounce((fieldId, value) => {
      setValue(fieldId, value)
    }, 100),
    [setValue],
  )

  useEffect(() => {
    return () => {
      debouncedSetValue.cancel()
    }
  }, [debouncedSetValue])

  const isFieldModified = useMemo(() => {
    return modifiedFields.some(f => f === field.id)
  }, [field.id, modifiedFields])

  const updateData = useCallback((formValue) => {
    if (isBulkUpdateModal && !isFieldModified) {
      return { ...field.formData, value: '' }
    }
    return { ...formValue, value: localValue }
  }, [field.formData, isBulkUpdateModal, isFieldModified, localValue])

  const checkIfModified = useCallback((value) => {
    if (field.formData.value !== value) {
      if (!modifiedFields.includes(field.id)) {
        setModifiedFields(currentArray => [...currentArray, field.id])
      }
    } else {
      if (!modifiedFields.includes(field.id)) {
        setModifiedFields(currentArray => [...currentArray, field.id])
      }
      const updatedModifiedArray = modifiedFields.filter(f => f.id !== field.id)
      setModifiedFields(updatedModifiedArray)
    }
  }, [field.formData.value, field.id, modifiedFields, setModifiedFields])

  const handleChange = useCallback((_, value) => {
    setLocalValue(value)
    setCurrentFieldValue(value)
    checkIfModified(value)

    if (field.field.type === 'TEXT' || field.field.type === 'text'
        || field.field.type === 'FLOAT' || field.field.type === 'float'
        || field.field.type === 'INT' || field.field.type === 'int'
        || field.field.type === 'BIGINT' || field.field.type === 'bigint'
        || field.field.type === 'MONEY' || field.field.type === 'money'
        || !field.field.type) {
      setValue(field.id, value)
    } else {
      debouncedSetValue(field.id, value)
    }
  }, [field.field.type, field.id, setValue, debouncedSetValue, setLocalValue, setCurrentFieldValue, checkIfModified])

  const handleValidation = useCallback((_, value) => {
    const fieldType = (field.field.type || '').toLowerCase()
    if (isBulkUpdateModal && fieldType === 'text' && (value === '' || value === null)) {
      setValue(field.id, '')
    }
    validateWithDependencies(
      field.id,
      field.formData.value,
      field.dependencies && field.dependencies.filter(d => d.type === 0),
      validateLabel,
    )
  }, [field.field.type, isBulkUpdateModal, setValue, field.id, field.formData.value, field.dependencies, validateLabel])

  const fieldName = useMemo(() => {
    return field.name.replace(/\s+/g, '-')
  }, [field.name])

  return (
    <div className={field.isDisabled ? styles.conditionallyDisabled : null} data-test-id={fieldName}>
      <p className={styles.label}>
        {field.name}
        {field.isRequired && !field.isDisabled && <span className={styles.required}>Required</span>}
      </p>
      <Parameter
        liveValidation
        simple
        className={styles[field.field.type]}
        dataTestId={fieldName}
        disabled={disabled || field.isDisabled}
        field={field.field}
        formData={updateData(field.formData)}
        label={field.name}
        onChange={handleChange}
        onValidation={handleValidation}
      />
    </div>
  )
}

LabelField.propTypes = labelFieldPropTypes
LabelField.defaultProps = labelFieldDefaultProps

export default LabelField
